/**
 * Utility functions for formatting values consistently across the application
 */

const _isFr = () => (typeof window !== 'undefined' && String(window.locale || 'en').toLowerCase().startsWith('fr'));

/**
 * Format a number as currency with $ sign
 * @param {number} value - The value to format as currency
 * @param {boolean} abbreviate - Whether to abbreviate large numbers (e.g., 1M, 2.5B)
 * @returns {string} The formatted currency string
 */
export const formatCurrency = (value, abbreviate = true) => {
  if (value === undefined || value === null) {
    return _isFr() ? '0\u00a0$' : '$0';
  }

  // Convert to number if it's a string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    console.warn('Invalid value passed to formatCurrency:', value);
    return _isFr() ? '0\u00a0$' : '$0';
  }

  const fr = _isFr();

  // For large numbers, abbreviate if requested
  if (abbreviate) {
    if (numValue >= 1000000000) {
      const n = (numValue / 1000000000).toFixed(1).replace('.', fr ? ',' : '.');
      return fr ? `${n}\u00a0G$` : `$${n}B`;
    }
    if (numValue >= 1000000) {
      const n = (numValue / 1000000).toFixed(1).replace('.', fr ? ',' : '.');
      return fr ? `${n}\u00a0M$` : `$${n}M`;
    }
    if (numValue >= 1000) {
      const n = (numValue / 1000).toFixed(1).replace('.', fr ? ',' : '.');
      return fr ? `${n}\u00a0k$` : `$${n}K`;
    }
  }

  // Format with locale-appropriate thousands separators
  const locale = fr ? 'fr-CA' : 'en-CA';
  const formatted = numValue.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return fr ? `${formatted}\u00a0$` : `$${formatted}`;
};

/**
 * Format a number as a percentage
 * @param {number} value - The value to format as percentage
 * @param {number} decimalPlaces - Number of decimal places to show (default 1)
 * @returns {string} The formatted percentage string
 */
export const formatPercentage = (value, decimalPlaces = 1) => {
  if (value === undefined || value === null) {
    return _isFr() ? '0\u00a0%' : '0%';
  }

  // Convert to number if it's a string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    console.warn('Invalid value passed to formatPercentage:', value);
    return _isFr() ? '0\u00a0%' : '0%';
  }

  const pct = numValue.toFixed(decimalPlaces);
  return _isFr() ? `${pct}\u00a0%` : `${pct}%`;
};

/**
 * Format a date in a consistent way
 * @param {string|Date} date - The date to format
 * @param {string} format - The format to use ('short', 'medium', 'long')
 * @returns {string} The formatted date string
 */
export const formatDate = (date, format = 'medium') => {
  if (!date) {
    return '';
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (!(dateObj instanceof Date) || isNaN(dateObj)) {
    console.warn('Invalid date passed to formatDate:', date);
    return '';
  }

  const options = {
    short: { month: 'numeric', day: 'numeric', year: '2-digit' },
    medium: { month: 'short', day: 'numeric', year: 'numeric' },
    long: { month: 'long', day: 'numeric', year: 'numeric' },
  };

  const locale = (typeof window !== 'undefined' && window.locale) || 'en';
  return dateObj.toLocaleDateString(locale, options[format] || options.medium);
};

export default {
  formatCurrency,
  formatPercentage,
  formatDate,
}; 